/**
 * Admin Portal "Store access" — the page's decisions (partner codes platform C4, 2026-09-27).
 *
 * Pure: no fetch, no React. The server (routers/admin_store_access.py,
 * services/store_credentials.py) validates everything again and is the authority; these
 * checks only save the owner a round trip and turn short codes into plain words.
 *
 * Codes PR-2 (2026-09-28): a discount offer is a (percentage, length) pair — a monthly discount
 * lasts 1, 3, 6 or 12 months, an annual one the first year — and each pair is its own store offer,
 * taking its own one of an App Store product's 10 offer-code slots. The slot meter counts EVERY
 * active offer on the product, Coach Cedar's own promotions included (the Summer50 referral
 * rewards), and the form refuses to switch an offer on where no slot is free.
 *
 * Codes PR-6 (2026-09-29): while a product's last clean read of its store is fresh (7 days —
 * "Refresh store offers", read-only store calls), the meter counts one slot per distinct offer
 * across our switched-on discounts and the store's active offers, each named by its owner (a menu
 * combo, a Summer50 reward, an offer acknowledged as Coach Cedar's own, or unknown) — the server's
 * switch-on check counts the same. A stale or missing read keeps the recorded meter. The "What
 * the stores sell" section lists every offer the stores hold and what does not match.
 */

import { termLabel, termChoiceLabel, type Period, type StoreKey } from './discountTermsLogic';

export type { StoreKey };

export interface LastTest {
  at: string;
  ok: boolean;
  code: string | null;
  by: string | null;
}

export interface StoreView {
  store: StoreKey;
  configured: boolean;
  secret_name: string;
  settings: Record<string, string>;
  set_at: string | null;
  set_by: string | null;
  last_test: LastTest | null;
}

export interface InventoryLevel {
  bucket_id: string;
  level_pct: number;
  /** The discount's length (migration 1845); absent from an older server. */
  months?: number;
  product_id: string;
  period: 'monthly' | 'annual';
  state: string;
  live: boolean;
  usable: number;
  allocated: number;
  redeemed: number;
  expired: number;
  next_expiry: string | null;
}

export interface InventoryAlert {
  id: string;
  title: string;
  message: string;
  type: string | null;
  created_at: string;
}

export interface InventoryView {
  floor: number;
  batch_size: number;
  retire_before_days: number;
  quarter_used: number;
  quarter_limit: number;
  levels: InventoryLevel[];
  batches: Array<{ id: string; bucket_id: string; status: string; number_of_codes: number;
    codes_imported: number; expires_on: string | null; error_code: string | null; created_at: string }>;
  alerts: InventoryAlert[];
}

export type Tone = 'grey' | 'amber' | 'green' | 'red';

export const STORE_TITLES: Record<StoreKey, string> = {
  app_store: 'App Store Connect',
  google_play: 'Google Play',
};

const CODE_WORDS: Record<string, string> = {
  ok: 'Connected.',
  not_configured: 'Nothing is saved yet.',
  unauthorized: 'The store refused the key (it may have been revoked or deleted). Create a new key and save it again.',
  forbidden: 'The store knows the key but it is not allowed to manage the app. On App Store Connect the key needs the '
    + 'App Manager role; on Google Play the service account must be invited to Coach Cedar in Play Console with the '
    + 'permissions in the steps below. A brand-new Play invite can take a few hours to start working.',
  not_found: 'The store could not find the app. Check the app ID (Apple) or the package name (Google).',
  rate_limited: 'The store asked us to slow down. Try again in a few minutes.',
  store_unavailable: 'The store is having trouble right now. Try again later.',
  network: 'Could not reach the store. Try again in a minute.',
  bad_key: 'The saved key could not sign a request. Save the key file again.',
  bad_id: 'The app ID or package name is not valid.',
  bad_response: 'The store answered with something unexpected. Try again; if it repeats, tell the agent.',
  secrets_access_denied: 'The server is not allowed to read keys from AWS Secrets Manager yet — the one-time AWS '
    + 'step is still owed.',
  secrets_error: 'AWS Secrets Manager did not answer. Try again in a minute.',
  error: 'Something went wrong while testing. Try again; if it repeats, tell the agent.',
};

/** Plain words for a test result code. */
export function codeWords(code: string | null | undefined): string {
  if (!code) return '';
  if (CODE_WORDS[code]) return CODE_WORDS[code];
  if (/^http_\d+$/.test(code)) return `The store answered ${code.slice(5)}. Try again; if it repeats, tell the agent.`;
  return `Failed (${code}).`;
}

/** The status chip on a store's card. */
export function storeChip(view: StoreView | undefined): { label: string; tone: Tone } {
  if (!view || !view.configured) return { label: 'Not configured yet', tone: 'grey' };
  if (!view.last_test) return { label: 'Saved — not tested yet', tone: 'amber' };
  if (view.last_test.ok) return { label: 'Connected', tone: 'green' };
  return { label: 'Connection failed', tone: 'red' };
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** "Set on <date> by <admin>" — or null when nothing is saved. */
export function setLine(view: StoreView | undefined): string | null {
  if (!view || !view.configured || !view.set_at) return null;
  return `Set on ${fmt(view.set_at)} by ${view.set_by ?? 'an admin'}.`;
}

/** "Last tested <date> by <admin>: <words>" — or null. */
export function testLine(view: StoreView | undefined): string | null {
  const t = view?.last_test;
  if (!t) return null;
  return `Last tested ${fmt(t.at)}${t.by ? ` by ${t.by}` : ''}: ${codeWords(t.code)}`;
}

/** The non-secret identifiers shown on a card, in reading order. */
export function identifierRows(view: StoreView | undefined): Array<{ label: string; value: string }> {
  if (!view || !view.configured) return [];
  const s = view.settings || {};
  const rows = view.store === 'app_store'
    ? [['Issuer ID', s.issuer_id], ['Key ID', s.key_id], ['App ID', s.app_id]]
    : [['Service account', s.client_email], ['Key ID', s.private_key_id], ['Project', s.project_id], ['Package', s.package_name]];
  return rows.filter(([, v]) => !!v).map(([label, value]) => ({ label: label as string, value: value as string }));
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The App Store form's own check before sending: a message, or null when it looks right. */
export function appStoreFormError(f: { issuerId: string; keyId: string; appId: string; privateKey: string }): string | null {
  if (!UUID_RE.test(f.issuerId.trim())) return 'The Issuer ID looks like 57246542-96fe-1a63-e053-0824d011072a.';
  if (!/^[A-Z0-9]{10}$/.test(f.keyId.trim().toUpperCase())) return 'The Key ID is 10 letters and digits.';
  if (f.appId.trim() && !/^[0-9]{6,12}$/.test(f.appId.trim())) return 'The app ID is a number (Coach Cedar is 6758776684).';
  if (!f.privateKey.includes('PRIVATE KEY')) return 'Choose the AuthKey_….p8 file you downloaded.';
  return null;
}

/** The Google Play form's own check before sending. */
export function googlePlayFormError(f: { serviceAccountJson: string; packageName: string }): string | null {
  if (f.packageName.trim() && !/^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/.test(f.packageName.trim())) {
    return 'The package name looks like com.coachcedar.app.';
  }
  let parsed: any;
  try {
    parsed = JSON.parse(f.serviceAccountJson);
  } catch {
    return 'Choose the service account key file (.json) Google Cloud downloaded.';
  }
  if (!parsed || parsed.type !== 'service_account') return 'That JSON is not a service-account key file.';
  return null;
}

/** A server refusal's message (never the key — the server never repeats it). */
export function refusalText(body: any, fallback: string): string {
  const d = body?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
}

export interface LevelRow {
  key: string;
  name: string;
  usable: number;
  allocated: number;
  redeemed: number;
  state: string;
  tone: Tone;
}

/** One row per App Store discount level; a live level below the floor is amber. */
export function levelRows(inv: InventoryView | undefined): LevelRow[] {
  if (!inv) return [];
  return inv.levels.map((l) => ({
    key: l.bucket_id,
    name: `${l.level_pct}% off${l.months != null ? ` (${termLabel(l.period, l.months)})` : ''} · ${l.product_id}`,
    usable: l.usable,
    allocated: l.allocated,
    redeemed: l.redeemed,
    state: l.live ? (l.usable < inv.floor ? 'Low' : 'Stocked') : 'Not live',
    tone: !l.live ? 'grey' : l.usable < inv.floor ? 'amber' : 'green',
  }));
}

/** "N of LIMIT one-time codes this quarter (x%)". */
export function quotaLine(inv: InventoryView | undefined): string | null {
  if (!inv) return null;
  const pct = inv.quarter_limit > 0 ? Math.round((inv.quarter_used / inv.quarter_limit) * 1000) / 10 : 0;
  return `${inv.quarter_used.toLocaleString('en-CA')} of ${inv.quarter_limit.toLocaleString('en-CA')} one-time codes asked of Apple this quarter (${pct}%).`;
}

/** Why the inventory is empty, in plain words. */
export function emptyInventoryText(inv: InventoryView | undefined, appStore: StoreView | undefined): string | null {
  if (!inv || inv.levels.some((l) => l.live)) return null;
  if (!appStore?.configured) {
    return 'No App Store discount level is live yet, and no App Store key is saved. Codes are created automatically '
      + 'once both are in place.';
  }
  return 'No App Store discount level is live yet. Codes are created automatically once a level is turned on.';
}

// ── Discount levels × store products (store_offer_buckets) ─────────────────────────────────

export interface Bucket {
  id: string;
  level_pct: number;
  /** How long the discount lasts: 1–12 months on a monthly product, always 12 on an annual one. */
  months: number;
  store: StoreKey;
  product_id: string;
  period: 'monthly' | 'annual';
  store_offer_id: string | null;
  state: 'pending' | 'active' | 'inactive' | 'failed';
  level_status?: string;
  offer_status?: string;
}

/** A store promotion that is not a partner discount but holds a slot on the product. */
export interface ExternalOffer { store: StoreKey; product_id: string; name: string; owner: string; kind?: string }

/** Who holds a slot (services/store_offer_buckets.SLOT_OWNERS). */
export type SlotOwner = 'menu' | 'summer50' | 'acknowledged' | 'unknown';

/** One slot on a product, named by its owner. */
export interface SlotHolder {
  owner: SlotOwner;
  label: string;
  state?: string | null;
  store_offer_id?: string | null;
  reference_name?: string | null;
  name?: string;
  level_pct?: number;
  months?: number;
  duration_months?: number | null;
  bucket_state?: string;
  observed_at?: string | null;
  /** Play: the base plan the offer sits on ('' on the App Store). */
  base_plan_id?: string | null;
  /** A discount switched on here that the store does not list as active: it still takes its slot. */
  in_store?: boolean;
  /** A Coach Cedar promotion the store does not list: its slot stays reserved. */
  reserved?: boolean;
}

/** Every active offer on one store product against the store's cap. */
export interface ProductSlots {
  store: StoreKey;
  product_id: string;
  period: string | null;
  cap: number;
  /** The meter: every bucket of ours with a store offer id (whatever its state) + the promotions. */
  used: number;
  free: number;
  /** What the server's switch-on check counts: our ACTIVE buckets + the promotions. */
  cap_used?: number;
  /** "as recorded in Coach Cedar — App Store Connect enforces the real limit". */
  basis?: string;
  ours: Array<{ level_pct: number; months: number; state?: string }>;
  external: ExternalOffer[];
  /** 'store': the store's own active offers (read from it); 'recorded': what Coach Cedar recorded. */
  source?: 'store' | 'recorded';
  /** When the product's offers were last read from its store (source 'store'). */
  synced_at?: string | null;
  /** What Coach Cedar recorded as taking a slot (source 'store' only). */
  recorded_cap_used?: number;
  /** Every slot, named by its owner. */
  owners?: SlotHolder[];
  /** The last clean read of the store is older than the store-truth window: back on the records. */
  stale?: boolean;
}

export interface BucketsView {
  buckets: Bucket[];
  levels: Array<{ pct: number; status: string }>;
  /** The (level, term) menu (migration 1845). */
  offers?: Array<{ pct: number; months: number; status: string; level_status: string }>;
  monthly_terms?: number[];
  products: Record<StoreKey, Array<{ product_id: string; period: string }>>;
  active_cap: Record<StoreKey, number>;
  slots?: ProductSlots[];
  external_offers?: ExternalOffer[];
}

export const BUCKET_STATE_TONE: Record<Bucket['state'], Tone> = {
  active: 'green', pending: 'amber', inactive: 'grey', failed: 'red',
};

/** The levels an admin may pick: the active ones on the menu, plus any level that already has a
 * bucket (a retired level's bucket must still be switchable off). The server refuses switching a
 * retired level ON. */
export function pickableLevels(view: BucketsView | undefined): number[] {
  const withBuckets = new Set((view?.buckets ?? []).map((b) => b.level_pct));
  return (view?.levels ?? []).filter((l) => l.status === 'active' || withBuckets.has(l.pct)).map((l) => l.pct);
}

/** The period of a store product ('monthly' / 'annual'), or null when unknown. */
export function productPeriod(view: BucketsView | undefined, store: StoreKey, productId: string): Period | null {
  const p = (view?.products?.[store] ?? []).find((x) => x.product_id === productId);
  return p ? (p.period as Period) : null;
}

/**
 * The lengths an admin may pick for a level on a product: an annual product is always 12 (the
 * first year); a monthly one offers the level's ACTIVE terms on the menu, plus any term that
 * already has a bucket for it (so it can still be switched off).
 */
export function pickableTerms(view: BucketsView | undefined, level: string, store: StoreKey, productId: string): number[] {
  const period = productPeriod(view, store, productId);
  if (period === 'annual') return [12];
  if (!/^\d+$/.test(level)) return [];
  const pct = Number(level);
  const fromMenu = (view?.offers ?? [])
    .filter((o) => o.pct === pct && o.status === 'active' && o.level_status === 'active').map((o) => o.months);
  const terms = new Set<number>(fromMenu.length ? fromMenu : (view?.monthly_terms ?? [1, 3, 6, 12]));
  for (const b of view?.buckets ?? []) {
    if (b.level_pct === pct && b.store === store && b.product_id === productId) terms.add(b.months);
  }
  return Array.from(terms).sort((a, b) => a - b);
}

/** "3 months (first 3 months)" for a picker; annual reads "12 months (first year)". */
export function termOptionLabel(months: number, period: Period | null): string {
  return period === 'annual' ? '12 months (first year)' : termChoiceLabel(months);
}

/** One product's slots, or undefined before the page has loaded them. */
export function slotsFor(view: BucketsView | undefined, store: StoreKey, productId: string): ProductSlots | undefined {
  return (view?.slots ?? []).find((s) => s.store === store && s.product_id === productId);
}

/** The meter's label: the count is what Coach Cedar recorded; the store console holds the truth. */
export function slotBasis(slot: ProductSlots): string {
  return slot.basis ?? (slot.store === 'app_store'
    ? 'as recorded in Coach Cedar — App Store Connect enforces the real limit'
    : 'as recorded in Coach Cedar — Play Console enforces the real limit');
}

/** "Menu combo: 15% off · first 3 months" · "Summer50 reward: …" · "Unknown: SPRING-TRIAL". */
export const SLOT_OWNER_TITLES: Record<SlotOwner, string> = {
  menu: 'Menu combo',
  summer50: 'Summer50 reward',
  acknowledged: 'Coach Cedar offer',
  unknown: 'Unknown',
};

export function slotHolderLabel(h: SlotHolder): string {
  const title = SLOT_OWNER_TITLES[h.owner] ?? SLOT_OWNER_TITLES.unknown;
  const notes: string[] = [];
  if (h.owner === 'menu' && h.bucket_state && h.bucket_state !== 'active') notes.push('switched off here');
  if (h.in_store === false) notes.push('not active in the store');
  if (h.reserved) notes.push('reserved, not listed in the store');
  if (h.base_plan_id) notes.push(`base plan ${h.base_plan_id}`);
  return `${title}: ${h.label}${notes.length ? ` (${notes.join('; ')})` : ''}`;
}

/** "the 8 Oct 2026 read" — a short date for a timestamp, or null. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/**
 * "8 of 10 offer slots used, as recorded in Coach Cedar — App Store Connect enforces the real limit:
 * 6 partner discounts (1 switched off here but still set up in the store), 2 Coach Cedar promotions
 * (FOUNDREF-50-MONTH, …)." Every offer we have recorded an id for counts, whatever its state here
 * (switching one off does not deactivate it in the store console), and so do Coach Cedar's own
 * promotions.
 */
export function slotLine(slot: ProductSlots | undefined): string | null {
  if (!slot) return null;
  if (slot.source === 'store') {
    const owners = slot.owners ?? [];
    const count = (o: SlotOwner) => owners.filter((h) => h.owner === o).length;
    const parts = [
      count('menu') ? `${count('menu')} partner discount${count('menu') === 1 ? '' : 's'}` : null,
      count('summer50') ? `${count('summer50')} Summer50 reward${count('summer50') === 1 ? '' : 's'}` : null,
      count('acknowledged') ? `${count('acknowledged')} acknowledged Coach Cedar offer${count('acknowledged') === 1 ? '' : 's'}` : null,
      count('unknown') ? `${count('unknown')} not recorded in Coach Cedar` : null,
    ].filter(Boolean);
    const when = shortDate(slot.synced_at);
    return `${slot.used} of ${slot.cap} offer slots used, ${slotBasis(slot)}${when ? ` (read ${when})` : ''}`
      + ` with what Coach Cedar switched on${parts.length ? `: ${parts.join(', ')}` : ''}.`;
  }
  const staleNote = slot.stale
    ? ` (The last read from the store${shortDate(slot.synced_at) ? `, ${shortDate(slot.synced_at)},` : ''} is too old to count — refresh store offers.)`
    : '';
  const off = slot.ours.filter((o) => o.state != null && o.state !== 'active').length;
  const parts = [`${slot.ours.length} partner discount${slot.ours.length === 1 ? '' : 's'}`
    + (off ? ` (${off} switched off here but still set up in the store)` : '')];
  if (slot.external.length) {
    parts.push(`${slot.external.length} Coach Cedar promotion${slot.external.length === 1 ? '' : 's'} `
      + `(${slot.external.map((e) => e.name).join(', ')})`);
  }
  return `${slot.used} of ${slot.cap} offer slots used, ${slotBasis(slot)}: ${parts.join(', ')}.${staleNote}`;
}

/** Another bucket of this store product already holding the offer id (one store offer is one
 * discount and one length — the server's offer_id_in_use), or undefined. */
export function offerIdHolder(view: BucketsView | undefined, store: StoreKey, productId: string, offerId: string,
  level: number, months: number): Bucket | undefined {
  const id = offerId.trim();
  if (!view || !id) return undefined;
  return view.buckets.find((b) => b.store === store && b.product_id === productId && b.store_offer_id === id
    && !(b.level_pct === level && b.months === months));
}

/** The server's words for that refusal (services/store_offer_buckets.offer_in_use_message). */
export function offerIdInUseText(holder: Bucket): string {
  return `That offer id is already recorded on this product for ${holder.level_pct}% off `
    + `(${termLabel(holder.period, holder.months)}). One store offer is one discount and one length: create a `
    + 'separate offer in the store console for this one, or correct the other row first.';
}

/** Why a product's slots matter, in the owner's words (design §4 (e)). */
export const SLOT_EXPLAINER =
  'Apple lets each subscription carry 10 active offer-code offers, shared by every partner and by Coach Cedar’s '
  + 'own promotions. Each discount-and-length pair takes one on every product it is switched on for; Google Play '
  + 'allows 50. Make a pair live only when a partner link needs it.';

/** Why an offer can't be switched on for a full product — the same facts the server refuses with. */
export function noFreeSlotText(slot: ProductSlots): string {
  const rule = slot.store === 'app_store'
    ? 'Apple lets each subscription carry 10 active offer-code offers, shared by every partner and by Coach Cedar’s own promotions.'
    : 'Google Play allows 50 active offers per subscription.';
  return `This product has no free offer slot — ${slotLine(slot)} ${rule} Switch another offer off on this product first.`;
}

/** The bucket form's own check before sending (the server checks again, incl. the caps). With the
 * page's view, it also refuses switching an offer on where the product has no free slot. */
export function bucketFormError(
  f: { level: string; store: string; productId: string; offerId: string; state: string; months?: string },
  view?: BucketsView,
): string | null {
  if (!/^\d+$/.test(f.level)) return 'Choose a discount level.';
  if (f.store !== 'app_store' && f.store !== 'google_play') return 'Choose a store.';
  if (!f.productId) return 'Choose a product.';
  if (f.months !== undefined && !/^\d+$/.test(f.months)) return 'Choose how long the discount lasts.';
  const offer = f.offerId.trim();
  if (offer && f.store === 'app_store' && !/^[A-Za-z0-9._-]{1,200}$/.test(offer)) {
    return "The offer id is letters, digits, '.', '_' or '-' only.";
  }
  if (offer && f.store === 'google_play' && !/^[a-z0-9][a-z0-9-]{0,62}$/.test(offer)) {
    return 'A Play offer id is lowercase letters, digits and hyphens (at most 63), starting with a letter or digit.';
  }
  if (f.state === 'active' && !f.offerId.trim()) return 'A level can only be switched on with the store’s offer id.';
  if (view) {
    const holder = offerIdHolder(view, f.store as StoreKey, f.productId, f.offerId, Number(f.level),
      f.months !== undefined ? Number(f.months) : 12);
    if (holder) return offerIdInUseText(holder);
  }
  if (f.state === 'active' && view) {
    const store = f.store as StoreKey;
    const months = f.months !== undefined ? Number(f.months) : 12;
    const already = view.buckets.some((b) => b.store === store && b.product_id === f.productId
      && b.level_pct === Number(f.level) && b.months === months && b.state === 'active');
    const slot = slotsFor(view, store, f.productId);
    // The server's switch-on rule counts our ACTIVE offers + the promotions (cap_used); the meter
    // (used) also counts offers switched off here, which the store console may still hold.
    if (!already && slot && (slot.cap_used ?? slot.used) >= slot.cap) return noFreeSlotText(slot);
  }
  return null;
}

/**
 * The App Store price-point rule (partner codes C5; owner rule 2026-09-27): Apple prices an offer
 * by a price point per country, not a percentage, so the owner picks, in EVERY country, the point
 * AT OR BELOW the exact {pct}%-off price. The real discount is then never less than the partner
 * offer sheet advertises ("{pct}% off"). Shown under the form while an App Store level is chosen.
 */
export function appStorePricePointHint(level: string, store: StoreKey, months?: number | null,
  period?: Period | null): string | null {
  if (store !== 'app_store' || !/^\d+$/.test(level)) return null;
  const length = period === 'annual'
    ? 'Create it as a 1-year offer. '
    : months != null
      ? `Create it as pay-as-you-go for ${termChoiceLabel(months)} (the ${termLabel('monthly', months)}), the length partners promise. `
      : '';
  return `${length}Pick the App Store price point at or below ${level}% off the regular price in every country, so the discount is never less than advertised.`;
}

/** "3 of 10 active" per store product, so the owner sees the cap before hitting it — every offer
 * on the product counts, Coach Cedar's own promotions included. */
export function activeCountLine(view: BucketsView | undefined, store: StoreKey, productId: string): string | null {
  if (!view || !productId) return null;
  const slot = slotsFor(view, store, productId);
  if (slot) return slotLine(slot);
  const n = view.buckets.filter((b) => b.store === store && b.product_id === productId && b.state === 'active').length;
  return `${n} of ${view.active_cap[store]} active offers on this product.`;
}

// ── What the stores sell (Codes PR-6, GET /store-offers) ────────────────────────────────────

export type FindingKind = 'active_bucket_offer_missing' | 'offer_on_other_base_plan' | 'active_bucket_offer_inactive'
  | 'duration_mismatch' | 'unknown_active_offer' | 'stale_read';

export interface StoreFinding {
  kind: FindingKind;
  store: StoreKey;
  product_id: string;
  store_offer_id: string | null;
  base_plan_id?: string | null;
  level_pct: number | null;
  months: number | null;
  period: string | null;
  observed_state: string | null;
  observed_months: number | null;
  reference_name: string | null;
  message: string;
}

export interface StoreRun {
  id: string;
  started_at: string | null;
  finished_at: string | null;
  by: string;
  status: string | null;
  products_ok: number;
  products_failed: number;
  mismatches: number;
}

export interface StoreTruthView {
  products: Array<{ store: StoreKey; product_id: string; period: string; synced_at: string | null;
    fresh?: boolean; base_plan_id?: string | null; offers: SlotHolder[] }>;
  findings: StoreFinding[];
  acknowledgements?: Array<{ store: StoreKey; product_id: string; base_plan_id: string; store_offer_id: string;
    label: string; acknowledged_at: string | null }>;
  max_age_days?: number;
  runs: StoreRun[];
  switches?: { store_offer_refresh?: boolean; store_offer_observed_slots?: boolean };
}

export const FINDING_TITLES: Record<FindingKind, string> = {
  active_bucket_offer_missing: 'Switched on, but the store does not list the offer',
  offer_on_other_base_plan: 'Switched on, but the offer is on another base plan',
  active_bucket_offer_inactive: 'Switched on, but the offer is not active in the store',
  duration_mismatch: 'The store sells another length',
  unknown_active_offer: 'An active offer Coach Cedar has not recorded',
  stale_read: 'Not read from the store recently',
};

/** Offers the page lets an admin acknowledge (unknown) or un-acknowledge (acknowledged). */
export function acknowledgeAction(o: SlotHolder): 'acknowledge' | 'undo' | null {
  if (o.owner === 'unknown' && (o.state === 'active' || o.state === 'unknown')) return 'acknowledge';
  if (o.owner === 'acknowledged') return 'undo';
  return null;
}

/** "Read 2026-09-29 by an admin: 10 of 10 products read, 2 things to look at." — or the first-use hint. */
export function lastRefreshLine(view: StoreTruthView | undefined): string {
  const run = view?.runs?.[0];
  if (!run) {
    return 'The stores have not been read yet. Press Refresh store offers to read what App Store Connect and '
      + 'Play Console actually sell (read-only: nothing in either store is changed).';
  }
  const when = shortDate(run.started_at) ?? 'recently';
  const who = run.by === 'cron' ? 'the nightly check' : 'an admin';
  if (run.status === 'unfinished') return `The last read (${when}, by ${who}) did not finish. Refresh again.`;
  const total = run.products_ok + run.products_failed;
  // Only 'ok' and 'partial' are a finished read (review of #4236): a run cut off by the page's
  // bound, one that read nothing, or one with no store key must never read as a clean success.
  if (run.status === 'timeout') {
    return `The last read (${when}, by ${who}) was cut off before it finished: ${run.products_ok} of ${total} `
      + 'products read, and nothing was compared. What it read is saved; refresh again.';
  }
  if (run.status === 'failed') {
    return `The last read (${when}, by ${who}) could not read any product. Refresh again; if that fails, test the store keys above.`;
  }
  if (run.status === 'not_configured') {
    return `The last read (${when}, by ${who}) found no store key saved: add them above, then refresh.`;
  }
  const findings = run.mismatches === 0 ? 'everything matches' : `${run.mismatches} thing${run.mismatches === 1 ? '' : 's'} to look at`;
  return `Last read ${when} by ${who}: ${run.products_ok} of ${total} products read, ${findings}.`;
}

/** A refresh's answer, in words. */
export function refreshResultText(summary: any): string {
  const status = summary?.status;
  if (status === 'busy') return 'Another read is already running; try again in a minute.';
  if (status === 'not_configured') return 'No store key is saved yet: add them above, then refresh.';
  const products = Array.isArray(summary?.store_offers?.products) ? summary.store_offers.products : [];
  const ok = products.filter((p: any) => p?.result === 'ok').length;
  const failed = products.filter((p: any) => p?.result !== 'ok');
  const n = summary?.store_offers?.mismatches ?? 0;
  const why = failed.length
    ? ` Not read: ${failed.map((p: any) => `${p.product_id} (${p.result})`).join(', ')} — the offers read before stay.`
    : '';
  return `Read ${ok} of ${products.length} products; ${n === 0 ? 'everything matches' : `${n} thing${n === 1 ? '' : 's'} to look at`}.${why}`;
}

/** "3 months" / "1 month" / "—" for an offer's length. */
export function offerLengthText(months: number | null | undefined): string {
  if (months == null) return '—';
  return months === 1 ? '1 month' : `${months} months`;
}
